import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { updateParticipantDocument } from '../../store/documentService';
import { DOCUMENT_TYPES_ERROR } from '../../utils/formValidation/loadingErrorMessageConstants';
import SingleSelect from '../formElements/SingleSelect';
import StaticField from '../formElements/StaticField';
import FormActions from '../ui/formActions/FormActions';
import LoadingSpinner from '../ui/LoadingSpinner';

import { validate } from './validateDocuments';

import app from '../../app.module.css';
import classes from './documents.module.css';

const EditDocument = ({ isDisabled, row, onClose }) => {
    const dispatch = useDispatch();

    const [typeId, setTypeId] = useState('');
    const [initialEntry, setInitialEntry] = useState('');
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const msg = `${row.filename} has been updated`;

    const documentTypes = useSelector(
        (state) => state.entities.directusService.documentTypeIdsAndNamesForContract
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!('typeId' in row)) return;
        setTypeId(row.typeId);
        setInitialEntry(row.typeId);
    }, [row]);

    useEffect(() => {
        if (!documentTypes?.length) setLoadingContentMessage(DOCUMENT_TYPES_ERROR);
    }, [documentTypes]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === msg) onFormExit();
    }, [successMessage]);

    // FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onTypeChange = (id) => {
        clearError('typeId');
        setTypeId(id);
    };

    const onFormExit = () => {
        setTypeId('');
        setInitialEntry('');
        setErrors({});
        setIsButtonDisabled(false);
        onClose();
    };
    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(typeId, initialEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateParticipantDocument(row.filename, currentParticipant.id, { typeId }, msg));
        onFormExit();
    };

    return !documentTypes?.length ? (
        <LoadingSpinner content={loadingContentMessage} />
    ) : (
        <div className={`${app.fadein} ${classes.documentForm}`}>
            <form onSubmit={onSubmit} data-testid="edit_document_form">
                <StaticField id="documentName" label="File Name" content={row.filename} />

                <div className={classes.optionInput}>
                    <SingleSelect
                        id="typeId"
                        label="Document Type"
                        placeholder="Select document type..."
                        disabled={isDisabled}
                        mandatory={true}
                        menuItems={documentTypes || []}
                        selectedId={typeId || ''}
                        selected={documentTypes.find((el) => el.id === typeId)}
                        error={errors.typeId}
                        onChange={(id) => onTypeChange(id)}
                    />
                </div>

                <FormActions
                    id="editDocumentTypeButton"
                    onClose={onSubmit}
                    onCancel={onFormExit}
                    btnText="Update"
                    disabled={isDisabled || isButtonDisabled}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditDocument.propTypes = {
    isDisabled: PropTypes.bool,
    row: PropTypes.object,
    onClose: PropTypes.func
};

export default EditDocument;
